import {
  anyContainingPermission,
  useAuthorizationRequired,
} from "../auth/WithAuthorizationRequired";
import { AppBar, IconButton, Toolbar, Typography } from "@mui/material";
import { Breakpoint, useBreakpoint } from "portal/utils/hooks/useBreakpoint";
import { buildPermission } from "portal/utils/auth";
import { capitalize, titleCase } from "portal/utils/strings";
import { classes } from "portal/theme/theme";
import { Drawer, NavItem } from "../Drawer";
import { FeatureFlag, useFeatureFlag } from "portal/utils/hooks/useFeatureFlag";
import { GlobalHotKeys, GlobalHotKeysProps, KeyMap } from "react-hotkeys";
import { GROUP_NAVIGATION } from "portal/utils/hotkeys";
import { Helmet } from "react-helmet";
import { isLocal, isTest } from "portal/utils/environment";
import { isUndefined } from "portal/utils/identity";
import { Link, useNavigate } from "react-router-dom";
import { LOCALSTORAGE_DRAWER_EXPANDED } from "portal/utils/localStorage";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { ThemeModeSwitch } from "../ThemeModeSwitch";
import { useCanSeeCarbonCropsPage } from "portal/views/carbonCrops/permissions";
import { useCanSeeCategoryTemplatesPage } from "portal/views/categoryTemplates/permissions";
import { useCanSeePlantProfilesPage } from "portal/views/plantProfiles/permissions";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { withErrorBoundary } from "../ErrorBoundary";
import { WithSkeleton } from "../WithSkeleton";
import AdminSettingsIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import AtkIcon from "common/images/icons/atk.svg?react";
import CarbonCropsIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import CarbonLogo from "portal/images/logo_ops_center.svg?react";
import CategoryTemplatesIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import CloseMenuIcon from "@mui/icons-material/MenuOpenOutlined";
import CustomerIcon from "@mui/icons-material/WorkOutlineOutlined";
import ExternalIcon from "@mui/icons-material/OpenInNewOutlined";
import FarmIcon from "portal/images/icons/farm.svg?react";
import FleetIcon from "portal/images/icons/slayer_side_outlined.svg?react";
import MenuIcon from "@mui/icons-material/MenuOutlined";
import OperatorIcon from "@mui/icons-material/TabletMac";
import ParentIcon from "@mui/icons-material/ArrowBackOutlined";
import PlantProfilesIcon from "@mui/icons-material/YardOutlined";
import React, { FunctionComponent, ReactNode } from "react";
import ReportIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import SupportIcon from "@mui/icons-material/HelpCenterOutlined";
import UploadIcon from "@mui/icons-material/CloudUploadOutlined";
import UsersIcon from "@mui/icons-material/GroupsOutlined";

interface Props {
  children?: ReactNode;
  hideDrawer?: boolean;
  hideTitle?: boolean;
  parentLink?: string;
  subtitle?: string;
  title?: string;
  pageTitle?: string;
}

const _Header: FunctionComponent<Props> = ({
  children,
  hideDrawer = false,
  hideTitle = false,
  parentLink,
  subtitle,
  title,
  pageTitle,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customer } = useSelf();

  const breakpoint = useBreakpoint();
  const isSmall = breakpoint <= Breakpoint.sm;

  const [isExpanded, setExpanded] = useLocalStorage<boolean>(
    LOCALSTORAGE_DRAWER_EXPANDED,
    false
  );

  const keyHandlers: GlobalHotKeysProps["handlers"] = {
    GO_SETTINGS: () => navigate(Path.SETTINGS),
    GO_ADMIN: () => navigate(Path.ADMIN),
    GO_CUSTOMERS: () => navigate(Path.CUSTOMERS),
    GO_FLEET: () => navigate(Path.FLEET),
    GO_ATK: () => navigate(Path.AUTOTRACTOR),
    GO_KNOWLEDGE: () => navigate(Path.KNOWLEDGE),
    GO_CATEGORY_TEMPLATES: () => navigate(Path.CATEGORY_TEMPLATES),
    GO_PLANT_PROFILES: () => navigate(Path.PLANT_PROFILES),
    GO_CARBON_CROPS: () => navigate(Path.CARBON_CROPS),
    GO_OFFLINE: () => navigate(Path.OFFLINE),
    GO_REPORTS: () => navigate(Path.REPORTS),
    GO_USERS: () => navigate(Path.USERS),
  };
  const keyMap: KeyMap = {};
  const drawerItems: NavItem[] = [];

  // settings
  const isSettingsAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.update,
        PermissionResource.users,
        PermissionDomain.self
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.users,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.users,
        PermissionDomain.all
      ),
    ],
  });
  if (isSettingsAuthorized) {
    drawerItems.push({
      label: t("views.settings.title"),
      href: Path.SETTINGS,
      icon: <SettingsIcon />,
    });

    keyMap.GO_SETTINGS = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: t("views.settings.title"),
      }),
      action: "keyup",
      sequence: "g s",
    };
  }

  // fleet
  const isRobotsAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots,
        PermissionDomain.customer
      ),
    ],
  });
  if (isRobotsAuthorized) {
    drawerItems.push({
      label: t("views.fleet.title"),
      icon: <FleetIcon />,
      href: Path.FLEET,
    });
    keyMap.GO_FLEET = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: t("views.fleet.title"),
      }),
      action: "keyup",
      sequence: "g f",
    };
  }

  // category templates
  const canSeeCategoryTemplatesPage = useCanSeeCategoryTemplatesPage();
  if (canSeeCategoryTemplatesPage) {
    drawerItems.push({
      label: t("views.categoryTemplates.title"),
      icon: <CategoryTemplatesIcon />,
      href: Path.CATEGORY_TEMPLATES,
    });
    keyMap.GO_CATEGORY_TEMPLATES = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: t("views.categoryTemplates.title"),
      }),
      action: "keyup",
      sequence: "g y",
    };
  }

  // plant profiles (customer-scoped)
  const canSeePlantProfilesPage = useCanSeePlantProfilesPage();
  if (canSeePlantProfilesPage) {
    const _title = t("views.plantProfiles.title");
    drawerItems.push({
      label: _title,
      icon: <PlantProfilesIcon />,
      href: Path.PLANT_PROFILES,
    });
    keyMap.GO_PLANT_PROFILES = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: _title,
      }),
      action: "keyup",
      sequence: "g p",
    };
  }

  // carbon crops (customer enablement — Carbon technical)
  const canSeeCarbonCropsPage = useCanSeeCarbonCropsPage();
  if (canSeeCarbonCropsPage) {
    drawerItems.push({
      label: t("views.carbonCrops.title"),
      icon: <CarbonCropsIcon />,
      href: Path.CARBON_CROPS,
    });
    keyMap.GO_CARBON_CROPS = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: t("views.carbonCrops.title"),
      }),
      action: "keyup",
      sequence: "g shift+c",
    };
  }

  // ATK
  const isUserAtkAuthorizedThroughCustomer =
    useAuthorizationRequired(
      anyContainingPermission(
        PermissionAction.read,
        PermissionResource.atk_equipment,
        PermissionDomain.customer
      )
    ) && customer?.hasAtk;
  const isUserAtkSuperuser = useAuthorizationRequired(
    anyContainingPermission(
      PermissionAction.read,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );
  if (isUserAtkAuthorizedThroughCustomer || isUserAtkSuperuser) {
    drawerItems.push({
      label: t("models.autotractor.autonomy"),
      icon: <AtkIcon className="size-6" />,
      href: Path.AUTOTRACTOR,
    });
    keyMap.GO_ATK = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: t("models.autotractor.autonomy"),
      }),
      action: "keyup",
      sequence: "g a",
    };
  }

  // farms
  const isFarmsAuthorized =
    useAuthorizationRequired(
      anyContainingPermission(
        PermissionAction.read,
        PermissionResource.farms,
        PermissionDomain.customer
      )
    ) && customer?.hasAtk;
  if (isFarmsAuthorized) {
    drawerItems.push({
      label: titleCase(t("models.farms.farm_other")),
      icon: <FarmIcon />,
      href: Path.FARMS,
    });
    keyMap.GO_FARMS = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: capitalize(t("models.farms.farm_other")),
      }),
      action: "keyup",
      sequence: "g a",
    };
  }

  // reports
  const { isEnabled: hasReports } = useFeatureFlag(FeatureFlag.REPORTS);
  const isReportsAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.reports,
        PermissionDomain.self
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.reports,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.reports,
        PermissionDomain.customer
      ),
    ],
  });
  if (hasReports && isReportsAuthorized) {
    drawerItems.push({
      label: titleCase(t("models.reports.report_other")),
      icon: <ReportIcon />,
      href: Path.REPORTS,
    });
    keyMap.GO_REPORTS = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: capitalize(t("models.reports.report_other")),
      }),
      action: "keyup",
      sequence: "g r",
    };
  }

  // customers
  const isCustomersAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );
  if (isCustomersAuthorized) {
    drawerItems.push({
      label: titleCase(t("models.customers.customer_other")),
      icon: <CustomerIcon />,
      href: Path.CUSTOMERS,
    });
    keyMap.GO_CUSTOMERS = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: capitalize(t("models.customers.customer_other")),
      }),
      action: "keyup",
      sequence: "g c",
    };
  }

  // users
  const isUsersAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.users,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.users,
        PermissionDomain.customer
      ),
    ],
  });
  if (isUsersAuthorized) {
    drawerItems.push({
      label: titleCase(t("models.users.user_other")),
      icon: <UsersIcon />,
      href: Path.USERS,
    });
    keyMap.GO_USERS = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: capitalize(t("models.users.user_other")),
      }),
      action: "keyup",
      sequence: "g u",
    };
  }

  // knowledge
  drawerItems.push({
    label: t("views.knowledge.title"),
    icon: <SupportIcon />,
    href: Path.KNOWLEDGE,
  });
  keyMap.GO_KNOWLEDGE = {
    group: GROUP_NAVIGATION,
    name: t("utils.actions.goToLong", {
      subject: t("views.knowledge.title"),
    }),
    action: "keyup",
    sequence: "g k",
  };

  // uploaded images
  const isUploadsUpdateAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.uploads,
      PermissionDomain.all
    )
  );
  if (isUploadsUpdateAuthorized) {
    drawerItems.push({
      label: t("views.offline.title"),
      icon: <UploadIcon />,
      href: Path.OFFLINE,
    });
    keyMap.GO_OFFLINE = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: t("views.offline.title"),
      }),
      action: "keyup",
      sequence: "g o",
    };
  }

  // admin
  const isAdminAuthorized = useAuthorizationRequired(
    anyContainingPermission(
      PermissionAction.read,
      PermissionResource.admin_cloud,
      PermissionDomain.all
    )
  );
  if (isAdminAuthorized) {
    drawerItems.push({
      label: t("views.admin.title"),
      icon: <AdminSettingsIcon />,
      href: Path.ADMIN,
    });
    keyMap.GO_ADMIN = {
      group: GROUP_NAVIGATION,
      name: t("utils.actions.goToLong", {
        subject: t("views.admin.title"),
      }),
      action: "keyup",
      sequence: "g d",
    };
  }

  // operator
  const operatorUrl = window._jsenv.REACT_APP_OPERATOR_URL;
  if (operatorUrl) {
    drawerItems.push({
      label: (
        // not need to localize proper noun
        // eslint-disable-next-line i18next/no-literal-string
        <div className="flex items-center">
          {t("views.operator.title")}
          <ExternalIcon className="ml-2 text-sm text-lighten-500" />
        </div>
      ),
      icon: <OperatorIcon />,
      href: operatorUrl,
    });
  }

  if (!pageTitle) {
    pageTitle = title;
    if (subtitle) {
      pageTitle += ` ${subtitle}`;
    }
  }
  return (
    <>
      <GlobalHotKeys keyMap={keyMap} handlers={keyHandlers} />
      {title && (
        <Helmet>
          <title>
            {/* no need to localize proper nouns */}
            {/* eslint-disable-next-line i18next/no-literal-string */}
            {pageTitle} - Carbon Robotics
          </title>
        </Helmet>
      )}
      <AppBar
        position="fixed"
        enableColorOnDark={true}
        className={classes("print:hidden")}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar className="w-full flex pl-4">
          {!isSmall && (
            <Link to={Path.FLEET}>
              <CarbonLogo className="h-8 mr-8 mt-1" />
            </Link>
          )}
          {!hideDrawer && isSmall && (
            <IconButton
              className="flex-shrink-0"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setExpanded(!isExpanded)}
            >
              {isExpanded ? <CloseMenuIcon /> : <MenuIcon />}
            </IconButton>
          )}

          {parentLink && (
            <IconButton
              className="flex-shrink-0"
              edge="start"
              color="inherit"
              aria-label="back to parent"
              component={Link}
              to={parentLink}
            >
              <ParentIcon />
            </IconButton>
          )}
          <WithSkeleton
            variant="rectangular"
            className="min-h-6 mr-4 whitespace-nowrap text-ellipsis overflow-hidden min-w-10 flex-shrink"
            success={!isUndefined(title)}
          >
            {hideTitle ? (
              <></>
            ) : (
              <Typography
                variant="h1"
                component="div"
                className="text-lg text-inherit"
              >
                {title}
              </Typography>
            )}
          </WithSkeleton>
          <div className="flex min-w-0 flex-1 items-center justify-between gap-4 overflow-hidden self-stretch">
            {children}
          </div>
          {(isLocal || isTest) &&
          window._jsenv.REACT_APP_FEATURE_LIGHT_DARK_THEME === "true" ? (
            <ThemeModeSwitch />
          ) : undefined}
        </Toolbar>
      </AppBar>
      {!hideDrawer && <Drawer items={drawerItems} />}
    </>
  );
};

export const Header = withErrorBoundary(
  {
    i18nKey: "components.header.failed",
    small: true,
  },
  _Header
);
